import { autoinject } from 'aurelia-framework';
import ReactDOM from 'react-dom';

import { Logger } from '~/utils/logger';

interface DebugPropertyCount {
	count: number;
	handle: number;
}

@autoinject
export abstract class ReactComponent {
	/**
	 * Reference to the container HTMLElement
	 */
	protected refContainer: HTMLElement;

	private hasBindExecuted: boolean = false;

	private hasElementToUnload: boolean = false;

	/**
	 * debugPropertyChangeCount is used when DEVELOPMENT is true to detect
	 * if suspecious re-rendering behaviour.
	 */
	private debugPropertyChangeCount: { [key: string]: DebugPropertyCount } =
		{};

	constructor(
		protected readonly CLASS_NAME: string,
		protected element: Element,
	) {
		if (!this.CLASS_NAME) {
			throw Error(
				`CLASS_NAME for ${this.constructor.name} must be defined!`,
			);
		}

		if (!element) {
			Logger.debug('Building element for', this.constructor.name);
			this.element = document.createElement('div');
			this.element.setAttribute('ref', 'refContainer');
			this.refContainer = this.element as HTMLElement;
		}
		this.element.classList.add(this.CLASS_NAME);
	}

	public getRefContainer() {
		return this.refContainer;
	}

	/**
	 * Render the DOM as soon as it's bound
	 */
	protected bind(bindingContext, overrideContext) {
		this.render();
		this.hasBindExecuted = true;
	}

	/**
	 * Component should be unmount on unbind
	 */
	protected detached() {
		// NOTE(Jae): 2020-04-29 - WEBUI-3196
		// This logic used to be in the "unbind()" method, but after investigating, it seems that
		// by that time, "refContainer" is always null, so the element will never be unloaded.
		// So we now call this in "detached()".
		if (this.refContainer && this.hasElementToUnload) {
			ReactDOM.unmountComponentAtNode(this.refContainer);
			this.hasElementToUnload = false;
		}
	}

	/**
	 * Once the Aurelia component is attached,
	 * manually attach the Aurelia <slots> to the rendered React DOM
	 */
	protected attached() {
		if (!this.hasBindExecuted) {
			if (DEVELOPMENT) {
				throw new Error(
					'Have you forgotten to call super.bind() on your subclass?',
				);
			}
			return;
		}
		this.attachSlots();
	}

	/**
	 * propertyChanged will fire anytime a `@bindable` property is changed.
	 *
	 * Properties that are simply `@observable` will need to manually trigger a change handler
	 * on render.
	 */
	protected propertyChanged(propertyName: string, value: any, oldValue: any) {
		if (!this.hasBindExecuted) {
			// Wait until bind() / first render has occured before calling
			// render. This fixes a timing issue where propertyChanged() can fire
			// before bind()
			return;
		}
		if (DEVELOPMENT) {
			// NOTE: Jake: 2019-11-13 - WEBUI-2690
			// Detect suspecious behaviour.
			// I tested this system by removing the @computedFrom() on slideoutContextMenu() for classic-form.ts
			if (this.debugPropertyChangeCount[propertyName] === undefined) {
				this.debugPropertyChangeCount[propertyName] = {
					count: 0,
					handle: 0,
				};
			}
			const debugInfo = this.debugPropertyChangeCount[propertyName];
			debugInfo.count += 1;
			if (debugInfo.handle === 0) {
				// NOTE: Jake: 2019-11-13 - WEBUI-2690
				// Tried various ms numbers.
				// 1000 = value changed 10 times.
				// 5000 = value changed 38-40 times. Thats definitely more wrong.
				const timePassedInMilliseconds = 5000;
				const allowedUpdatesPerTimePassed = 10;
				const prevCount: number = debugInfo.count;
				debugInfo.handle = window.setTimeout(() => {
					const diff = debugInfo.count - prevCount;
					if (diff >= allowedUpdatesPerTimePassed) {
						Logger.warn(
							`Suspecious Behaviour: "${this.constructor.name}.${propertyName}" has changed its value ${diff} times after ${timePassedInMilliseconds}ms triggering a React re-render (componentDidUpdate calls within your React component).\n\nMaybe @computedFrom is not being used correctly?`,
						);
					}
					debugInfo.handle = 0;
				}, timePassedInMilliseconds);
			}
		}
		this.render();
	}

	/**
	 * Helper function that calls `ReactDOM.render` to render the element to `.react-component`
	 * @param e React DOM
	 */
	protected renderReactDOM(e: JSX.Element): void {
		// NOTE: Jake: 2019-10-30 - WEBUI-2656
		// We used to also check if parentNode was null/undefined
		// but we CANT do that without breaking the ProgressBar on
		// Batch Processing Centre as it renders on an element that
		// isn't attached to the DOM.
		if (!this.refContainer) {
			// NOTE: Jake: 2019-10-22
			// Ignore React render if the router has blown away
			// the DOM / the element we're trying to render under
			return;
		}
		ReactDOM.render(e, this.refContainer);
		this.hasElementToUnload = true;
	}

	/**
	 * Manually attach the slots in to the React component
	 */
	protected attachSlots() {
		try {
			const view = this.element.au.controller.view;
			const slots = view.slots;

			Object.keys(slots).forEach((slotKey) => {
				const slotName = slots[slotKey].name;
				const slotElem = this.element.querySelector(
					`div[data-slot="${slotName}"]`,
				);
				if (slotElem) {
					slots[slotKey].children.forEach((childElem) => {
						slotElem.replaceWith(childElem);
					});
				} else {
					Logger.error('Unable to attach to slot: ', slotName);
				}
			});
		} catch (e) {
			Logger.error('Unable to attach the component slots\n', e);
		}
	}

	/**
	 * Must be implemented
	 */
	protected abstract render(): void;
}
